import { Cubit } from "blac-next";
import type { TourDefinition, TourGuideState } from "./types";

const STORAGE_PREFIX = "tourGuide.seen.";

const IDLE_STATE: TourGuideState = {
  activeTourId: null,
  currentStepIndex: 0,
  steps: [],
  isTransitioning: false
};

export interface TourGuideStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

const noopStorage: TourGuideStorage = {
  getItem: () => null,
  setItem: () => {}
};

export interface AnchorMeta {
  borderRadius?: string;
}

export default class TourGuideCubit extends Cubit<TourGuideState> {
  static keepAlive = true;

  private tours = new Map<string, TourDefinition>();
  private anchors = new Map<string, AnchorMeta>();
  private storage: TourGuideStorage = noopStorage;

  constructor() {
    super({ ...IDLE_STATE });
  }

  setStorage = (storage: TourGuideStorage): void => {
    this.storage = storage;
  };

  // ── Anchor registry ──────────────────────────────────

  isAnchorRegistered = (name: string): boolean => this.anchors.has(name);

  getAnchorMeta = (name: string): AnchorMeta | undefined =>
    this.anchors.get(name);

  registerAnchor = (name: string, meta: AnchorMeta = {}): void => {
    this.log("anchor registered", { anchor: name });
    this.anchors.set(name, meta);
    const { activeTourId, steps, currentStepIndex } = this.state;
    if (activeTourId && steps[currentStepIndex]?.anchorName === name) {
      this.log("anchor matches active step, re-rendering", { anchor: name });
      this.patch({});
    }
  };

  unregisterAnchor = (name: string): void => {
    this.log("anchor unregistered", { anchor: name });
    this.anchors.delete(name);
  };

  // ── Tour registry ────────────────────────────────────

  registerTour = (tour: TourDefinition): void => {
    this.log("tour registered", { tourId: tour.id });
    this.tours.set(tour.id, tour);
  };

  // ── Tour lifecycle ───────────────────────────────────

  startTour = async (tourId: string, force = false): Promise<void> => {
    this.log("startTour called", { tourId, force });

    if (this.state.activeTourId) {
      if (!force) {
        this.log("startTour blocked — tour already active", {
          activeTourId: this.state.activeTourId
        });
        return;
      }
      this.log("force-resetting active tour");
      this.patch({ ...IDLE_STATE });
    }
    if (!force && this.hasSeenTour(tourId)) {
      this.log("startTour skipped — already seen", { tourId });
      return;
    }

    const tour = this.tours.get(tourId);
    if (!tour) {
      this.log("startTour failed — tour not registered", { tourId });
      return;
    }

    const steps = tour.resolveSteps();
    if (steps.length === 0) {
      this.log("startTour skipped — no steps resolved", { tourId });
      return;
    }

    this.log("starting tour", {
      tourId,
      totalSteps: steps.length,
      anchors: steps.map((s) => s.anchorName)
    });

    this.patch({
      activeTourId: tourId,
      currentStepIndex: 0,
      steps,
      isTransitioning: true
    });

    this.log("running runBefore for step 0", {
      anchor: steps[0].anchorName
    });
    await this.runHook(steps[0].runBefore);
    await this.delay(200);
    this.patch({ isTransitioning: false });
    this.log("step 0 active", { anchor: steps[0].anchorName });
  };

  next = async (): Promise<void> => {
    const { steps, currentStepIndex } = this.state;
    this.log("next called", { currentStepIndex, totalSteps: steps.length });

    if (currentStepIndex >= steps.length - 1) {
      this.log("last step reached — completing tour");
      await this.completeTour();
      return;
    }

    this.patch({ isTransitioning: true });
    await this.waitForFrame();

    this.log("running runAfter for step", {
      step: currentStepIndex,
      anchor: steps[currentStepIndex]?.anchorName
    });
    await this.runHook(steps[currentStepIndex]?.runAfter);

    const nextIndex = currentStepIndex + 1;
    this.log("running runBefore for step", {
      step: nextIndex,
      anchor: steps[nextIndex]?.anchorName
    });
    await this.runHook(steps[nextIndex]?.runBefore);
    await this.delay(200);

    this.patch({
      currentStepIndex: nextIndex,
      isTransitioning: false
    });
    this.log("step active", {
      step: nextIndex,
      anchor: steps[nextIndex]?.anchorName
    });
  };

  dismiss = async (): Promise<void> => {
    const { steps, currentStepIndex, activeTourId } = this.state;
    this.log("dismiss called", { activeTourId, step: currentStepIndex });
    this.patch({ isTransitioning: true });
    await this.waitForFrame();
    this.log("running runAfter for dismissed step", {
      step: currentStepIndex,
      anchor: steps[currentStepIndex]?.anchorName
    });
    await this.runHook(steps[currentStepIndex]?.runAfter);
    this.patch({ ...IDLE_STATE });
    this.log("tour dismissed");
  };

  completeTour = async (): Promise<void> => {
    const { activeTourId, steps, currentStepIndex } = this.state;
    this.log("completeTour called", { activeTourId, step: currentStepIndex });
    this.patch({ isTransitioning: true });
    await this.waitForFrame();
    this.log("running runAfter for final step", {
      step: currentStepIndex,
      anchor: steps[currentStepIndex]?.anchorName
    });
    await this.runHook(steps[currentStepIndex]?.runAfter);

    if (activeTourId) {
      this.markTourSeen(activeTourId);
      this.log("tour marked as seen", { tourId: activeTourId });
    }

    this.patch({ ...IDLE_STATE });
    this.log("tour completed");
  };

  // ── Persistence ──────────────────────────────────────

  private hasSeenTour(tourId: string): boolean {
    return this.storage.getItem(`${STORAGE_PREFIX}${tourId}`) === "true";
  }

  private markTourSeen(tourId: string): void {
    this.storage.setItem(`${STORAGE_PREFIX}${tourId}`, "true");
  }

  // ── Helpers ──────────────────────────────────────────

  private log(_message: string, _data?: Record<string, unknown>): void {
    // logging disabled
  }

  private waitForFrame(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async runHook(
    hook: (() => void | Promise<void>) | undefined
  ): Promise<void> {
    if (hook) {
      await hook();
    }
  }
}
